import useSWR, { mutate } from 'swr'
import { apiFetch } from '@/utils'

const AVAILABLE_KEY = '/v1/billing/payment-methods/available'
const SETTINGS_KEY = '/v1/billing/payment-methods/settings'

export interface PaymentConfig {
	tokenEnabled: boolean
	cardEnabled: boolean
}

const DEFAULT_CONFIG: PaymentConfig = {
	tokenEnabled: true,
	cardEnabled: true,
}

// Backend returns { TOKEN: bool, CARD: bool } — normalize to our camelCase shape.
function normalize(raw: any): PaymentConfig {
	const cfg = raw?.data ?? raw ?? {}
	return {
		tokenEnabled: cfg?.TOKEN !== false,
		cardEnabled: cfg?.CARD !== false,
	}
}

async function fetchConfig(path: string): Promise<PaymentConfig> {
	const res = await apiFetch(path, { withCredentials: true })
	if (!res.ok) {
		// Gracefully degrade if backend isn't reachable
		if (res.status === 404) return DEFAULT_CONFIG
		throw new Error('Failed to fetch payment config')
	}
	return normalize(await res.json())
}

/**
 * Global payment method configuration. Controls which payment methods
 * (SECH token, credit card, or both) are accepted site-wide.
 *
 * Pass `admin: true` from admin screens to read from the admin settings
 * endpoint (which forces a cache refresh on the backend) and invalidate
 * both cached keys on update.
 */
export function usePaymentConfig(options: { admin?: boolean } = {}) {
	const key = options.admin ? SETTINGS_KEY : AVAILABLE_KEY

	const { data, error, isLoading } = useSWR<PaymentConfig>(key, fetchConfig, {
		revalidateOnFocus: false,
		dedupingInterval: 30_000,
		shouldRetryOnError: false,
		fallbackData: DEFAULT_CONFIG,
	})

	const config: PaymentConfig = data ?? DEFAULT_CONFIG

	const updateConfig = async (next: PaymentConfig): Promise<PaymentConfig> => {
		const res = await apiFetch(SETTINGS_KEY, {
			method: 'PUT',
			body: JSON.stringify({ TOKEN: next.tokenEnabled, CARD: next.cardEnabled }),
			withCredentials: true,
		})
		if (!res.ok) {
			const err = await res.json().catch(() => null)
			throw new Error(err?.message || 'Failed to update payment config')
		}
		const updated = normalize(await res.json())
		// Keep both cache keys in sync so users and admins see the new state.
		await mutate(SETTINGS_KEY, updated, false)
		await mutate(AVAILABLE_KEY, updated, false)
		return updated
	}

	return {
		config,
		tokenEnabled: config.tokenEnabled,
		cardEnabled: config.cardEnabled,
		isLoading,
		error,
		updateConfig,
		refetch: () => mutate(key),
	}
}

/**
 * Returns a human-readable reason if the given payment method type is
 * not currently accepted, or null if it is allowed.
 */
export function getPaymentMethodBlockedReason(
	type: 'TOKEN' | 'CARD',
	config: PaymentConfig,
): string | null {
	if (type === 'TOKEN' && !config.tokenEnabled) {
		return 'SECH Token payments are currently disabled. Please use a credit or debit card.'
	}
	if (type === 'CARD' && !config.cardEnabled) {
		return 'Credit card payments are currently disabled. Please pay with SECH Token.'
	}
	return null
}
